import { useMemo, type CSSProperties } from 'react';
import { Building2, Pencil, Trash2, TrendingUp, TrendingDown } from 'lucide-react';
import { useI18n } from '@/i18n/use-i18n';
import { getIntlLocale } from '@/i18n/locale';
import { AssetClassCode, FlatAsset } from '@/types/assets';
import './asset-card.css';

interface AssetCardProps {
  assetData: FlatAsset;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function AssetCard({
  assetData,
  onEdit,
  onDelete,
}: AssetCardProps) {
  const { strings, locale } = useI18n();
  const t = strings.targetPortfolio.card;
  const intlLocale = getIntlLocale(locale);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intlLocale, {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 2,
      }),
    [intlLocale],
  );

  const hasPrice = assetData.price != null && assetData.price > 0;

  const gainAbsolute = hasPrice && assetData.averagePrice > 0
    ? ((assetData.price ?? 0) - assetData.averagePrice) * assetData.quantity
    : null;

  const gainPercent = hasPrice && assetData.averagePrice > 0
    ? (((assetData.price ?? 0) / assetData.averagePrice) - 1) * 100
    : null;

  const isPositive = gainAbsolute != null && gainAbsolute >= 0;

  return (
    <div
      className="asset-card"
      style={{ '--accent-color': assetData.classColor } as CSSProperties}
    >
      <div className="asset-card__header">
        {assetData.logoUrl ? (
          <img className="asset-card__logo" src={assetData.logoUrl} alt={assetData.ticker} />
        ) : assetData.classCode === AssetClassCode.FII ? (
          <span className="asset-card__logo asset-card__logo--fii">
            <Building2 size={16} />
          </span>
        ) : (
          <span className="asset-card__logo asset-card__logo--fallback">
            {assetData.ticker.slice(0, 2)}
          </span>
        )}
        <span className="asset-card__ticker">{assetData.ticker}</span>
        {assetData.className && (
          <span className="asset-card__badge">{assetData.className}</span>
        )}
        <div className="asset-card__actions">
          {onEdit && (
            <button
              type="button"
              className="asset-card__action"
              onClick={onEdit}
              aria-label={`Editar ${assetData.ticker}`}
            >
              <Pencil size={15} />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="asset-card__action asset-card__action--danger"
              onClick={onDelete}
              aria-label={`Remover ${assetData.ticker}`}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </div>

      {assetData.shortName && (
        <p className="asset-card__name">{assetData.shortName}</p>
      )}

      <div className="asset-card__body">
        <div className="asset-card__field">
          <span className="asset-card__label">{t.quantity}</span>
          <span className="asset-card__value">{assetData.quantity}</span>
        </div>
        <div className="asset-card__field">
          <span className="asset-card__label">{t.averagePrice}</span>
          <span className="asset-card__value">
            {assetData.averagePrice > 0 ? currencyFormatter.format(assetData.averagePrice) : t.priceUnavailable}
          </span>
        </div>
        <div className="asset-card__field">
          <span className="asset-card__label">{t.currentPrice}</span>
          <span className="asset-card__value">
            {hasPrice ? currencyFormatter.format(assetData.price ?? 0) : t.priceUnavailable}
          </span>
        </div>
        <div className="asset-card__field">
          <span className="asset-card__label">
            {t.total}
            {assetData.amountInvestedIsEstimated && (
              <span
                className="asset-card__estimated-badge"
                title={t.estimatedTotal}
              >
                ~
              </span>
            )}
          </span>
          <span className="asset-card__value">
            {assetData.amountInvested != null ? currencyFormatter.format(assetData.amountInvested) : t.priceUnavailable}
          </span>
        </div>
      </div>

      {gainAbsolute != null && gainPercent != null && (
        <div className="asset-card__footer">
          <div className={`asset-card__gain ${isPositive ? 'asset-card__gain--positive' : 'asset-card__gain--negative'}`}>
            <span className="asset-card__gain-icon">
              {isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            </span>
            <span>
              {currencyFormatter.format(Math.abs(gainAbsolute))} ({isPositive ? '+' : ''}{gainPercent.toFixed(2)}%)
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
